import PropTypes from 'prop-types'
import { useState } from 'react'
import { format, isValid } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'
import { Calendar } from '@components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover'
import { Button } from '@components/ui/button'
import { Label } from '@components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select'
import { cn } from 'lib/utils'

const pad = (n) => String(n).padStart(2, '0')
const hours = Array.from({ length: 24 }, (_, i) => pad(i))
const minutes = Array.from({ length: 4 }, (_, i) => pad(i * 15))

export const AdasDateTimePicker = ({ label, value, onChange, disabled, disableFuture, disablePast, className, ...rest }) => {
  const [open, setOpen] = useState(false)

  const parsedDate = value ? new Date(value) : undefined
  const validDate = parsedDate && isValid(parsedDate) ? parsedDate : undefined

  const currentHour = validDate ? pad(validDate.getHours()) : '00'
  const currentMinute = validDate ? pad(Math.round(validDate.getMinutes() / 15) * 15) : '00'

  const handleDateSelect = (selected) => {
    if (!selected) return
    const updated = new Date(selected)
    updated.setHours(parseInt(currentHour), parseInt(currentMinute))
    onChange && onChange(updated)
    setOpen(false)
  }

  const handleTimeChange = (part, val) => {
    const base = validDate ? new Date(validDate) : new Date()
    if (part === 'hour') base.setHours(parseInt(val))
    if (part === 'minute') base.setMinutes(parseInt(val))
    onChange && onChange(base)
  }

  const disabledMatcher = []
  if (disableFuture) disabledMatcher.push({ after: new Date() })
  if (disablePast) disabledMatcher.push({ before: new Date() })

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label && <Label className='text-xs font-medium'>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant='outline' disabled={disabled} className='w-full justify-start font-normal border-custom-borderInput text-left'>
            <CalendarIcon className='mr-2 h-4 w-4' />
            {validDate ? format(validDate, 'MM/dd/yyyy HH:mm') : <span className='text-text-secondary'>Pick date and time</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='start'>
          <Calendar
            mode='single'
            selected={validDate}
            onSelect={handleDateSelect}
            disabled={disabledMatcher.length ? disabledMatcher : undefined}
            initialFocus
          />
          <div className='flex items-center gap-2 p-3 border-t border-divider'>
            <Select value={currentHour} onValueChange={(v) => handleTimeChange('hour', v)}>
              <SelectTrigger className='w-16'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {hours.map((h) => (
                  <SelectItem key={h} value={h}>
                    {h}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className='text-text-secondary'>:</span>
            <Select value={currentMinute} onValueChange={(v) => handleTimeChange('minute', v)}>
              <SelectTrigger className='w-16'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {minutes.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

AdasDateTimePicker.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  disableFuture: PropTypes.bool,
  disablePast: PropTypes.bool,
  className: PropTypes.string
}
